/** @jsxImportSource @emotion/react */

import { COLORS } from '@btime/constants'
import { css } from '@emotion/react'
import { isEmpty } from 'ramda'
import { useTranslation } from 'react-i18next'
import ExtDayPicker, { DateUtils, locale as dayPickerLocale } from 'react-day-picker'
import React from 'react'
import moment from 'moment'

import { Text } from '../../presentation/Text'
import Center from '../../helpers/Center'
import DayPickerHeader from './DayPickerHeader'
import Link from '../../actions/Link'
import Separator from '../../helpers/Separator'
import { useSessionPermission } from '@btime/react-session'
import { getLocale } from '../../../utils/locale/locale'

const WEEKDAYS_SHORT = {
  pt: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
  es: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
}
const WEEKDAYS_LONG = {
  pt: [
    'Domingo',
    'Segunda-feira',
    'Terça-feira',
    'Quarta-feira',
    'Quinta-feira',
    'Sexta-feira',
    'Sábado',
  ],
  es: [
    'Domingo',
    'Lunes',
    'Martes',
    'Miércoles',
    'Jueves',
    'Viernes',
    'Sábado',
  ],
  en: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ]
}

const style = css`
  text-align: center;
  font-family: 'SF Pro Display', 'Nunito', sans-serif;
  max-width: 20rem;
  margin: 0 auto;
  color: ${COLORS.dark2};

  & .DayPicker-wrapper,
  & .DayPicker {
    outline-style: none;
  }

  & .DayPicker-NavButton--next {
    background: red;
    right: 0.6rem;
  }

  & .DayPicker-Week,
  & .DayPicker-WeekdaysRow {
    display: flex;
    align-items: center;
    justify-content: space-around;
  }

  & .DayPicker-Day,
  & .DayPicker-Weekday {
    outline-style: none;
    flex-basis: 14%;
    padding: 0.6rem;
  }

  & .DayPicker-Weekday,
  & .DayPicker-Weekday abbr[title] {
    font-size: 0.6rem;
    color: ${COLORS.dark3};
    text-decoration: none;
    text-transform: uppercase;
    font-weight: 600;
  }

  & .DayPicker-Day {
    cursor: pointer;
  }

  & .DayPicker-Day--disabled {
    background-color: #f0f0f0;
    pointer-events: none;
    opacity: 0.5;
  }

  & .DayPicker-Day--outside {
    visibility: hidden;
  }

  &.range .DayPicker-Day--selected:not(.DayPicker-Day--outside) {
    background: ${COLORS.light1};
    border-radius: 0;
  }
  &:not(.range) .DayPicker-Day--selected:not(.DayPicker-Day--outside),
  &.range .DayPicker-Day--end:not(.DayPicker-Day--outside),
  &.range .DayPicker-Day--start:not(.DayPicker-Day--outside) {
    background: ${COLORS.purple};
    font-weight: 600;
    color: white;
  }

  &:not(.range) .DayPicker-Day--selected:not(.DayPicker-Day--outside) {
    border-radius: 5px;
  }

  &.range .DayPicker-Day--end:not(.DayPicker-Day--outside) {
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
  }
  &.range .DayPicker-Day--start:not(.DayPicker-Day--outside) {
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
  }
`

// TODO: Refactor
const DayPicker = ({ useRange, onChange, value, ...props }) => {
  const locale = getLocale(true)
  value = !useRange && !!value ? moment(value).toDate() : value
  let defaultValue = value
  const emptyValue = useRange ? [] : undefined
  const emptyLocalValue = useRange ? { to: undefined, from: undefined } : undefined
  if (useRange) {
    if (!value || isEmpty(value)) {
      defaultValue = emptyLocalValue
    } else {
      let [from, to] = value
      defaultValue = { from, to }
    }
  }

  const { t } = useTranslation()
  const blockPastDates = useSessionPermission('blockPastDates')
  const [localValue, setLocalValue] = React.useState(defaultValue)
  const { from, to } = useRange ? localValue : {}
  const [month, setMonth] = React.useState((useRange ? from : value) || new Date())

  const modifiers = useRange ? { start: from, end: to } : undefined
  const selectedDays = useRange ? [from, { from, to }] : value
  const hasAnyValue = useRange ? !!from : localValue

  const clearDate = () => {
    setLocalValue(emptyLocalValue)
    onChange(emptyValue)
  }

  const handleMonthChange = (n, date) => () => {
    const newDate = DateUtils.addMonths(date, n)
    setMonth(newDate)
  }

  const handleChange = day => {
    if (!useRange) {
      setLocalValue(day)
      return onChange(moment(day).startOf('day').toDate())
    } else {
      const newValue = DateUtils.addDayToRange(day, localValue)
      setLocalValue(newValue)
      if (!!newValue && newValue.from && newValue.to) {
        const formattedFrom = moment(newValue.from).startOf('day').toDate()
        const formattedTo = moment(newValue.to).endOf('day').toDate()
        onChange([formattedFrom, formattedTo])
      }
    }
  }

  return (
    <div>
      <DayPickerHeader setMonth={setMonth} month={month} handleMonthChange={handleMonthChange} />
      <ExtDayPicker
        locale={getLocale(true)}
        weekdaysLong={WEEKDAYS_LONG[locale]}
        weekdaysShort={WEEKDAYS_SHORT[locale]}
        css={style}
        className={useRange ? 'range' : ''}
        month={month}
        onDayClick={handleChange}
        modifiers={modifiers}
        selectedDays={selectedDays}
        captionElement={() => false}
        disabledDays={blockPastDates ? { before: new Date() } : {}}
        {...props}
      />

      <Separator weight={4} />
      <Center>
        {useRange && (
          <Text>
            {from ? moment(from).format('DD MMM YYYY') : t('selectInitDate')} -{' '}
            {to ? moment(to).format('DD MMM YYYY') : from && t('selectEndDate')}
          </Text>
        )}
        <Separator weight={3} />
        {hasAnyValue && (
          <Link onClick={clearDate}>
            <Text color={COLORS.red}>{t('cleanDate')}</Text>
          </Link>
        )}
      </Center>
    </div>
  )
}

export default DayPicker
